import { computed, reactive, ref } from 'vue'

import { useQuery, useMutation } from '@tanstack/vue-query'
import { defineStore } from 'pinia'

import type { IPaginatedResponse } from '@/apiClient'
import { handleApiErrorToast } from '@/apiClient'
import { GptApi } from '@/apiClient/modules/gpt'
import { applyGptPinStateChange } from '@/apiClient/queries/gptCacheUtils'
import type { IGpt, IGptData, IGptListFilters } from '@/apiClient/types/gpt'
import { queryClient } from '@/lib/queryClient'

import { useToast } from '../toast'

/**
 * The "Welcome to EverGPT" GPT, pinned for new users. The pin itself is seeded by the
 * backend; this list sorts it to the top of the sidebar's pinned GPTs and marks it as
 * the one to unpin once the user has started their first chat with it.
 */
export const AUTO_PIN_GPT_IDS = ['da58128b-9134-4c09-a9b9-7e9eb9950c6b']

export const useGptStore = defineStore('gpt', () => {
  //#region VueQuery

  /**
   * Vue Query hook for fetching GPTs with filters
   * @param filters - Optional filters to apply to the GPT list query
   */
  const useGptsQuery = (filters?: Partial<IGptListFilters>) => {
    return useQuery<IPaginatedResponse<IGpt>>({
      queryKey: ['gpts', filters],
      queryFn: ({ signal }) => GptApi.list(filters, { signal }),
      staleTime: 1000 * 60, // 1 minute
      enabled: false,
    })
  }

  /**
   * Vue Query mutation for creating a GPT
   */
  const useCreateGptMutation = () => {
    return useMutation({
      mutationFn: (data: IGptData) => GptApi.create(data),
      onSuccess: (newGpt) => {
        // Invalidate and refetch GPTs list
        queryClient.invalidateQueries({ queryKey: ['gpts'] })
        // Optimistically update local state
        allGpts.value.push(newGpt)
        gpts.value.push(newGpt)
        useToast().onSuccess('Your GPT was successfully created.')
      },
      onError: handleApiErrorToast,
    })
  }

  /**
   * Vue Query mutation for updating a GPT
   */
  const useUpdateGptMutation = () => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: IGptData }) =>
        GptApi.update(id, data),
      onSuccess: (
        updatedGpt,
        variables: { id: string; data: IGptData; ignoreToast?: boolean },
      ) => {
        // Invalidate and refetch GPTs list
        queryClient.invalidateQueries({ queryKey: ['gpts'] })
        // Update local state
        updateGptInStore(updatedGpt)
        if (!variables.ignoreToast) {
          useToast().onSuccess('Your GPT was successfully updated.')
        }
      },
      onError: handleApiErrorToast,
    })
  }

  /**
   * Vue Query mutation for deleting a GPT
   */
  const useDeleteGptMutation = () => {
    return useMutation({
      mutationFn: (id: string) => GptApi.remove(id),
      onSuccess: (_, id) => {
        // Invalidate and refetch GPTs list
        queryClient.invalidateQueries({ queryKey: ['gpts'] })
        // Remove from local state
        gpts.value = gpts.value.filter((gpt) => gpt.id !== id)
        allGpts.value = allGpts.value.filter((gpt) => gpt.id !== id)
      },
      onError: handleApiErrorToast,
    })
  }

  //#endregion

  const getSelectedGpt = (gptId: string) => {
    return allGpts.value.find((gpt) => gpt.id === gptId)
  }

  const isLoading = ref(false)
  const isLoadingCreate = ref(false)
  const isLoadingUpdate = ref(false)
  const isLoadingDelete = ref(false)
  const isLoadingShare = ref(false)

  /**
   * This is a new approach I'm playing around with
   * An object tracking pending promises for each API call
   */
  const pendingPromises = reactive({
    fetch: ref<ReturnType<typeof GptApi.list>>(),
    get: ref<ReturnType<typeof GptApi.get>>(),
    create: ref<ReturnType<typeof GptApi.create>>(),
    update: ref<ReturnType<typeof GptApi.update>>(),
    delete: ref<ReturnType<typeof GptApi.remove>>(),
  })

  // TODO: Use these loaders instead of individual loading flags
  // const loaders = reactive({
  //   isLoadingFetch: computed(() => !!pendingPromises.fetch),
  //   isLoadingGet: computed(() => !!pendingPromises.get),
  //   isLoadingCreate: computed(() => !!pendingPromises.create),
  //   isLoadingUpdate: computed(() => !!pendingPromises.update),
  //   isLoadingDelete: computed(() => !!pendingPromises.delete),
  // })

  /**
   * All GPTs visible to the user.
   * Storing it in state as it is needed on a global level.
   */
  const gpts = ref<IGpt[]>([])
  /**
   * All GPTs to keep in state. This will need to be optimised in the future.
   */
  // TODO: remove this since it was a hack
  // @deprecated
  const allGpts = ref<IGpt[]>([])
  const pinnedGpts = computed(() =>
    allGpts.value
      .filter((gpt) => gpt.pinned)
      .filter(
        (gpt, index, arr) => arr.findIndex((g) => g.id === gpt.id) === index,
      )
      .sort((a, b) => {
        const aIsAutoPinned = AUTO_PIN_GPT_IDS.includes(a.id)
        const bIsAutoPinned = AUTO_PIN_GPT_IDS.includes(b.id)

        // If both are auto-pinned or both are not auto-pinned, maintain original order
        if (aIsAutoPinned === bIsAutoPinned) {
          return 0
        }

        // Auto-pinned GPTs should come first
        return aIsAutoPinned ? -1 : 1
      }),
  )

  const createGpt = (data: IGptData) => {
    if (isLoading.value) return Promise.resolve()

    isLoadingCreate.value = true
    return GptApi.create(data)
      .then((res) => {
        gpts.value.push(res)
        allGpts.value.push(res)
        useToast().onSuccess('Your GPT was successfully created.')
        return res
      })
      .finally(() => {
        isLoadingCreate.value = false
      })
  }

  const updateGpt = (data: IGpt, ignoreToast?: boolean) => {
    if (isLoadingUpdate.value) return Promise.resolve()

    isLoadingUpdate.value = true
    return GptApi.update(data.id, data as unknown as IGptData)
      .then((res) => {
        updateGptInStore(res)
        if (!ignoreToast) {
          useToast().onSuccess('Your GPT was successfully updated.')
        }
        return res
      })
      .finally(() => {
        isLoadingUpdate.value = false
      })
  }

  const togglePin = (id: string) => {
    if (isLoading.value) return Promise.resolve()

    isLoading.value = true
    return GptApi.togglePin(id)
      .then(({ pinned }) => {
        applyGptPinStateChange(id, pinned)
        patchGptPinned(id, pinned)
        return pinned
      })
      .finally(() => {
        isLoading.value = false
      })
  }

  const updateGptInStore = (gpt: IGpt) => {
    const index = allGpts.value.findIndex((g) => g.id === gpt.id)
    if (index !== -1) {
      allGpts.value[index] = gpt
    }
    const index2 = gpts.value.findIndex((g) => g.id === gpt.id)
    if (index2 !== -1) {
      gpts.value[index2] = gpt
    }
  }

  const patchGptPinned = (id: string, pinned: boolean) => {
    const patch = (gpt: IGpt) =>
      gpt.id === id ? { ...gpt, is_pinned: pinned, pinned } : gpt

    gpts.value = gpts.value.map(patch)
    allGpts.value = allGpts.value.map(patch)
  }

  const refreshGpt = (uuid: string) => {
    return GptApi.get(uuid)
      .then((res) => {
        updateGptInStore(res)
        return res
      })
      .catch(handleApiErrorToast)
  }

  const fetchGpts = (filters?: Partial<IGptListFilters>) => {
    if (pendingPromises.fetch) return pendingPromises.fetch

    pendingPromises.fetch = GptApi.list(filters).then((res) => {
      gpts.value = res.items
      return res
    })

    return pendingPromises.fetch
  }

  const deleteGpt = (id: string) => {
    if (isLoadingDelete.value) return Promise.resolve()

    isLoadingDelete.value = true
    return GptApi.remove(id)
      .then(() => {
        gpts.value = gpts.value.filter((gpt) => gpt.id !== id)
        allGpts.value = allGpts.value.filter((gpt) => gpt.id !== id)
      })
      .catch(handleApiErrorToast)
      .finally(() => {
        isLoadingDelete.value = false
      })
  }

  return {
    isLoading,
    isLoadingCreate,
    isLoadingUpdate,
    isLoadingDelete,
    isLoadingShare,
    gpts,
    allGpts,
    pinnedGpts,
    getSelectedGpt,
    createGpt,
    updateGpt,
    togglePin,
    updateGptInStore,
    patchGptPinned,
    refreshGpt,
    fetchGpts,
    deleteGpt,
    // Vue Query hooks
    useGptsQuery,
    useCreateGptMutation,
    useUpdateGptMutation,
    useDeleteGptMutation,
  }
})
